import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ArrowDirection} from './ArrowDirection';


@Component({
  selector: 'app-health-score-guage',
  templateUrl: './health-score-guage.component.html',
  styleUrls: ['./health-score-guage.component.scss'],
})
export class HealthScoreGuageComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() animated: boolean = true;
  @Input() animationDuration: number = 1500;
  @Input() score: number = 0;
  @Input() arrowDirection?: ArrowDirection;
  @Input() percentageChanged?: string | null;
  @Input() showInfoButton?: boolean = false;
  @Input() activeDevices?: number = 0;


  @Output() infoClick = new EventEmitter<void>();
  @Output() activeDeviceClick = new EventEmitter<void>();


  lines: { x1: number, y1: number, x2: number, y2: number, isActive: boolean, colour: string }[] = [];
  markers: { x: number, y: number, x1: number, y1: number, x2: number, y2: number, text: string }[] = [];
  points: { x1: number, y1: number, x2: number, y2: number }[] = [];
  displayedScore: number = 0;
  private readonly radius = 280; // New radius (double the previous 100)
  private readonly centerX = 300; // New center x-coordinate (double the previous 150)
  private readonly centerY = 300; // New center y-coordinate (double the previous 150)
  private animationFrameId: number | null = null;

  private startTime: number = 0;

  constructor() {

  }

  ngOnInit() {
    this.renderControl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.startAnimation();
  }


  ngAfterViewInit(): void {
    // Ensure the component is fully initialized before starting animation
    this.renderControl();
    this.startAnimation();
  }


  public startAnimation() {
    if (this.animated) {
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
      }

      this.startTime = performance.now();
      this.animate();
    }
    else {
      this.displayedScore = this.score;
      this.generateLines();
    }
  }

  generateLines(): void {
    const totalLines = 65; // Total lines, each representing 2.5 points
    const angleSpan = 270; // Total degrees of the arc
    const angleStep = angleSpan / (totalLines - 1); // Angle between lines
    const rotationOffset = 135; // Rotation offset to start from bottom left

    this.lines = []; // Clear previous lines
    for (let i = 0; i < totalLines; i++) {
      // Calculate the angle for the current line
      const angle = (rotationOffset + angleStep * i) * (Math.PI / 180);
      const x1 = this.centerX + this.radius * Math.cos(angle);
      const y1 = this.centerY + this.radius * Math.sin(angle);
      const x2 = this.centerX + (this.radius - 20) * Math.cos(angle); // Updated thickness
      const y2 = this.centerY + (this.radius - 20) * Math.sin(angle);

      var percPlot = i / totalLines * 100;

      var colour = this.getColor(percPlot);
      this.lines.push({
        x1,
        y1,
        x2,
        y2,
        isActive: percPlot <= this.displayedScore, // Mark lines as active based on score
        colour
      });
    }
  }

  generateMarkers(): void {
    const totalMarkers = 100; // Markers for every 10 points from 0 to 100
    const angleSpan = 270; // Total degrees of the arc
    const angleStep = angleSpan / (totalMarkers - 1); // Angle between markers
    const rotationOffset = 135; // Rotation offset to start from bottom left
    this.markers = []; // Clear previous markers

    for (let i = 0; i <= totalMarkers; i++) {
      const value = i * (100 / totalMarkers); // Marker value
      const angle = (rotationOffset + angleStep * i) * (Math.PI / 180);
      const x = this.centerX + (this.radius - 90) * Math.cos(angle); // Position the marker closer to the center
      const y = this.centerY + (this.radius - 90) * Math.sin(angle);
      const x1 = this.centerX + (this.radius - 40) * Math.cos(angle); // Position the marker closer to the center
      const y1 = this.centerY + (this.radius - 40) * Math.sin(angle);
      const x2 = this.centerX + (this.radius - 60) * Math.cos(angle); // Updated thickness
      const y2 = this.centerY + (this.radius - 60) * Math.sin(angle);
      if (value % 25 == 0 || value == 100) {
        this.markers.push({
          x,
          y,
          x1, y1, x2, y2,
          text: value.toString()
        });
      }
    }
  }

  generatePoints(): void {
    const totalMarkers = 100; // Markers for every 10 points from 0 to 100
    const angleSpan = 270; // Total degrees of the arc
    const angleStep = angleSpan / (totalMarkers - 1); // Angle between markers
    const rotationOffset = 135; // Rotation offset to start from bottom left

    this.points = []; // Clear previous markers

    for (let i = 0; i < totalMarkers; i++) {
      const value = i * (100 / totalMarkers); // Marker value
      const angle = (rotationOffset + angleStep * i) * (Math.PI / 180);
      const x1 = this.centerX + (this.radius - 40) * Math.cos(angle); // Position the marker closer to the center
      const y1 = this.centerY + (this.radius - 40) * Math.sin(angle);
      const x2 = this.centerX + (this.radius - 50) * Math.cos(angle); // Updated thickness
      const y2 = this.centerY + (this.radius - 50) * Math.sin(angle);

      if (value % 5 == 0) {
        this.points.push({
          x1, y1, x2, y2
        });
      }
    }
  }

  getColor(index: number): string {
    if (index <= 15) {
      return this.getRadient(index, 0, 15, '#20ad6e', '#db0000');
    }
    else if (index > 15 && index <= 30) {
      return this.getRadient(index, 15, 30, '#db0000', '#f3a101');
    }
    else if (index > 30 && index <= 55) {
      return this.getRadient(index, 30, 55, '#f3a101', '#f3cc00');
    }
    else if (index > 55 && index <= 70) {
      return this.getRadient(index, 55, 70, '#f3cc00', '#89ed8d');
    }
    else if (index > 70 && index <= 85) {
      return this.getRadient(index, 70, 85, '#89ed8d', '#02bb09');
    }
    else {
      return this.getRadient(index, 85, 100, '#02bb09', '#008005');
    }
  }

  getRadient(index: number, min: number, max: number, startColor: string, endColor: string): string {
    if (index < 0 || index > 100) {
      throw new RangeError('Index must be between 0 and 100');
    }

    const startRgb = this.hexToRgb(startColor);
    const endRgb = this.hexToRgb(endColor);

    const steps = max - min;
    const stepSize = [
      (endRgb[0] - startRgb[0]) / steps,
      (endRgb[1] - startRgb[1]) / steps,
      (endRgb[2] - startRgb[2]) / steps
    ];

    const rgb = [
      Math.round(startRgb[0] + stepSize[0] * (index - min)),
      Math.round(startRgb[1] + stepSize[1] * (index - min)),
      Math.round(startRgb[2] + stepSize[2] * (index - min))
    ];

    return this.rgbToHex(rgb as [number, number, number]);
  }

  hexToRgb(hex: string): [number, number, number] {
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b];
  }

  rgbToHex(rgb: [number, number, number]): string {
    return `#${rgb[0].toString(16).padStart(2, '0')}${rgb[1].toString(16).padStart(2, '0')}${rgb[2].toString(16).padStart(2, '0')}`;
  }

  onActiveDeviceClick(): void {
    this.activeDeviceClick.emit();
  }

  onInfoClick(): void {
    this.infoClick.emit();
  }

  private animate() {
    const currentTime = performance.now();
    const elapsedTime = currentTime - this.startTime;
    const progress = Math.min(elapsedTime / this.animationDuration, 1);
    this.displayedScore = Math.round(progress * this.score);
    this.generateLines();

    if (progress < 1) {
      this.animationFrameId = requestAnimationFrame(() => this.animate());
    }
  }

  private renderControl() {
    this.generateLines();
    this.generateMarkers();
    this.generatePoints();
  }
}

